import { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { listImportBatches, uploadImport, confirmImport, getImportBatch } from '@/lib/api'
import { ImportUploader } from '@/components/import/ImportUploader'
import { ColumnMappingStep } from '@/components/import/ColumnMappingStep'
import { ImportBatchRow } from '@/components/import/ImportBatchRow'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { ImportUploadResponse } from '@/schemas'

type Step = 'idle' | 'upload' | 'mapping' | 'confirming'

export function Imports() {
  const qc = useQueryClient()
  const [step, setStep] = useState<Step>('idle')
  const [uploadResponse, setUploadResponse] = useState<ImportUploadResponse | null>(null)
  const [mappings, setMappings] = useState<Record<string, string>>({})
  const [pollingBatchId, setPollingBatchId] = useState<string | null>(null)

  const { data: batchesData, isLoading: batchesLoading } = useQuery({
    queryKey: ['imports'],
    queryFn: listImportBatches,
    refetchInterval: pollingBatchId ? 3000 : false,
  })

  // Stop polling once the active batch reaches a terminal state
  useEffect(() => {
    if (!pollingBatchId || !batchesData) return
    const batch = batchesData.batches.find((b) => b.batch_id === pollingBatchId)
    if (batch && (batch.status === 'completed' || batch.status === 'failed')) {
      setPollingBatchId(null)
    }
  }, [batchesData, pollingBatchId])

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return uploadImport(fd)
    },
    onSuccess: (res) => {
      setUploadResponse(res)
      // Auto-detect likely field mappings (case-insensitive column name match)
      const auto: Record<string, string> = {}
      for (const col of res.detected_columns) {
        const lower = col.toLowerCase().replace(/[\s_-]/g, '_')
        const match = FIELD_MAP[lower]
        auto[col] = match ?? '__skip__'
      }
      setMappings(auto)
      setStep('mapping')
    },
    onError: () => toast.error('Upload failed. Please try again.'),
  })

  const confirmMutation = useMutation({
    mutationFn: () => {
      if (!uploadResponse) throw new Error('No upload response')
      const column_mappings = Object.entries(mappings)
        .filter(([, field]) => field !== '__skip__')
        .map(([file_column, lead_field]) => ({ file_column, lead_field }))
      return confirmImport(uploadResponse.batch_id, { column_mappings })
    },
    onSuccess: (res) => {
      toast.success('Import started.')
      setPollingBatchId(res.batch_id)
      qc.invalidateQueries({ queryKey: ['imports'] })
      setStep('idle')
      setUploadResponse(null)
      setMappings({})
    },
    onError: () => toast.error('Could not confirm import.'),
  })

  const handleMappingChange = useCallback((col: string, field: string) => {
    setMappings((prev) => ({ ...prev, [col]: field }))
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Imports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload a CSV or XLSX file to import leads in bulk.
          </p>
        </div>
        {step === 'idle' && (
          <Button size="sm" onClick={() => setStep('upload')}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New import
          </Button>
        )}
      </div>

      {/* Upload / mapping wizard */}
      {step !== 'idle' && (
      <div className="rounded-lg border bg-card p-5 space-y-4">
        {step === 'upload' && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Step 1. Upload file</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep('idle')}>
                Cancel
              </Button>
            </div>
            <ImportUploader
              onFile={(file) => uploadMutation.mutate(file)}
              uploading={uploadMutation.isPending}
            />
          </>
        )}

        {step === 'mapping' && uploadResponse && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Map columns</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep('idle')
                  setUploadResponse(null)
                }}
              >
                Cancel
              </Button>
            </div>

            {/* 10-row preview */}
            <div className="overflow-x-auto rounded-md border bg-muted/30">
              <p className="px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                Preview. First {Math.min(uploadResponse.preview.length, 10)} rows
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      {uploadResponse.detected_columns.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResponse.preview.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-b last:border-0">
                        {uploadResponse.detected_columns.map((col) => (
                          <td
                            key={col}
                            className="px-3 py-1.5 text-muted-foreground truncate max-w-[160px]"
                          >
                            {row[col] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ColumnMappingStep
              fileColumns={uploadResponse.detected_columns}
              previewRows={uploadResponse.preview}
              mappings={mappings}
              onChange={handleMappingChange}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
              >
                {confirmMutation.isPending ? 'Starting import…' : 'Confirm import'}
              </Button>
            </div>
          </>
        )}
      </div>
      )}

      {/* Past batches */}
      <div className="rounded-lg border bg-card">
        <div className="px-5 py-3 border-b">
          <h2 className="text-sm font-semibold">Import history</h2>
        </div>
        {batchesLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !batchesData?.batches.length ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            No import batches yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">File</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Rows</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Imported</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Skipped</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Failed</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Progress</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Created</th>
                </tr>
              </thead>
              <tbody>
                {batchesData.batches.map((batch) => (
                  <ImportBatchRow key={batch.batch_id} batch={batch} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Heuristic auto-mapping: normalised file column names → lead fields
const FIELD_MAP: Record<string, string> = {
  first_name: 'first_name',
  firstname: 'first_name',
  last_name: 'last_name',
  lastname: 'last_name',
  surname: 'last_name',
  email: 'email',
  email_address: 'email',
  linkedin: 'linkedin_profile_url',
  linkedin_url: 'linkedin_profile_url',
  linkedin_profile_url: 'linkedin_profile_url',
  title: 'current_title',
  job_title: 'current_title',
  current_title: 'current_title',
  company: 'current_company',
  current_company: 'current_company',
  headline: 'headline',
  location: 'location',
  industry: 'industry',
  phone: 'phone',
  phone_number: 'phone',
  summary: 'summary',
}
